import {Clock, Wallet, Coins} from "lucide-react";
import {FC} from "react";
import RoundedIcon from "./RoundedIcon";
import Card from "../Card";
import {ShreddingCoinsData} from "@/@types/home";

interface TotalCostsProps {
  shreddingCoins: ShreddingCoinsData | undefined;
}

const TotalCosts: FC<TotalCostsProps> = ({shreddingCoins}) => {
  const formatNumber = (num: number): string => {
    return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") || "0";
  };
  
  const formatTime = (minutes: number): string => {
    if (!minutes) return "0m";
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };
  
  if (!shreddingCoins) {
    return (
      <Card borderColor="[#6F6AF8]" bgColor="[#6F6AF8]" minHeight="[24rem]">
        <div className="flex flex-col justify-center items-center h-full p-5">
          <RoundedIcon color="#fff" height="h-16" width="w-16">
            <Wallet className="text-white h-8 w-8"/>
          </RoundedIcon>
          
          <div className="flex flex-col items-center mt-6 text-center">
            <h4 className="font-medium tracking-wide text-sm text-white mb-2">
              No Coins Data Available
            </h4>
            <p className="text-white text-xs opacity-75">
              Statistics will appear here once data is available
            </p>
          </div>
        </div>
      </Card>
    );
  }
  
  return (
    <Card borderColor="[#6F6AF8]" bgColor="[#6F6AF8]" minHeight="[24rem]">
      <div className="flex flex-col justify-center items-center p-5">
        <RoundedIcon color="#fff" height="h-16" width="w-16">
          <Wallet className="text-white h-8 w-8"/>
        </RoundedIcon>
        
        <div className="flex flex-col items-center mt-3 mb-2">
          <h4 className="font-medium tracking-wide text-xs text-white">
            Credits Overview
          </h4>
          <h3 className="font-bold tracking-wide inputoutput text-2xl text-white mt-1">
            {formatNumber(shreddingCoins.totalCoins)} <span className="text-sm">used</span>
            <span className="px-1 text-sm">/</span>
            <span className="text-white text-lg mt-1">
              {formatNumber(shreddingCoins.remainingCoins)} <span className="text-sm">remaining</span>
            </span>
          </h3>
        </div>
        
        <div
          className="w-full mt-3 border-[1px] mx-2 py-3 px-2 rounded-md bg-[#9C98FA] flex items-center justify-center">
          <div className="flex justify-between items-center">
            <Clock className="text-white mr-3"/>
            <div className="flex flex-col">
              <span className="text-sm text-white font-bold inputoutput tracking-wide">
                Generation Time
              </span>
              <span className="text-sm inputoutput text-white">
                avg <strong>{formatTime(shreddingCoins.generationTiming?.averageMinutes)}</strong>
                <span className="px-1">/</span>
                <span
                  className="opacity-75">fastest: {formatTime(shreddingCoins.generationTiming?.fastestMinutes)}</span>
              </span>
            </div>
          </div>
        </div>
        
        <div
          className="w-full mt-3 border-[1px] mx-2 py-3 px-2 rounded-md bg-[#9C98FA] flex items-center justify-center">
          <div className="flex justify-between items-center">
            <Coins className="text-white mr-3"/>
            <div className="flex flex-col">
              <span className="text-sm text-white font-bold inputoutput tracking-wide">
                Credits Stats
              </span>
              <span className="text-sm inputoutput text-white">
                <strong>{shreddingCoins.averageCoinsPerShred || 0}</strong> avg /
                <strong>{formatNumber(shreddingCoins.maxCoinsForShred)}</strong> max
              </span>
            </div>
          </div>
        </div>
        
        <p className="text-white text-xs mt-3 text-center inputoutput tracking-wide">
          {shreddingCoins.description || "No description available"}
        </p>
      </div>
    </Card>
  );
};

export default TotalCosts;